// src/hooks/useSupabaseInquiryIntake.js
// ============================================================================
// Hook: useSupabaseInquiryIntake
// Manages inquiry_intake_messages CRUD + approve/reject workflow
// Follows existing useSupabase.js patterns (snake_case DB ↔ camelCase App)
//
// RBAC:
//   admin  → full CRUD, can approve/reject
//   supervisor → read-only (SELECT policy in RLS)
//   user   → no access (blocked by RLS)
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';

// ── Field mapping: snake_case (DB) → camelCase (App) ────────────────────
const mapIntakeFromDb = (row) => ({
  id: row.id,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
  gmailMessageId: row.gmail_message_id,
  gmailThreadId: row.gmail_thread_id,
  gmailLabel: row.gmail_label,
  fromEmail: row.from_email,
  fromName: row.from_name,
  subject: row.subject,
  receivedAt: row.received_at,
  bodyText: row.body_text,
  bodyHtml: row.body_html,
  attachments: row.attachments || [],
  parseResult: row.parse_result || {},
  parseStatus: row.parse_status,
  parseError: row.parse_error,
  linkedInquiryId: row.linked_inquiry_id,
  // v2: Forward-detection columns
  forwarderEmail: row.forwarder_email,
  forwarderName: row.forwarder_name,
  originalFromEmail: row.original_from_email,
  originalFromName: row.original_from_name,
  originalSubject: row.original_subject,
  originalReceivedAt: row.original_received_at,
});

// ── Main hook ───────────────────────────────────────────────────────────
// Options:
//   enabled   — when false, skip the initial auto-fetch (default: true)
//   countOnly — when true, the auto-fetch only loads needsReviewCount via a
//               lightweight head-count query instead of the full list. Used by
//               the nav badge in App.js so it doesn't pull every intake row.
export const useSupabaseInquiryIntake = ({ enabled = true, countOnly = false } = {}) => {
  const [intakeMessages, setIntakeMessages] = useState([]);
  const [needsReviewCount, setNeedsReviewCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ── Fetch "needs review" intake messages ───────────────────────────
  // Queries intake where parse_status='needs_review', joined to inquiries
  // where platform_type='email_forward' to stay consistent.
  const fetchNeedsReview = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Step 1: Fetch intake messages with needs_review status
      const { data: intakeData, error: intakeError } = await supabase
        .from('inquiry_intake_messages')
        .select('*')
        .eq('parse_status', 'needs_review')
        .order('received_at', { ascending: false });

      if (intakeError) throw intakeError;
      if (!intakeData || intakeData.length === 0) {
        setIntakeMessages([]);
        return { success: true, data: [] };
      }

      // Step 2: Fetch linked inquiries to confirm platform_type filter
      const linkedIds = intakeData
        .map((r) => r.linked_inquiry_id)
        .filter(Boolean);

      let inquiriesMap = {};
      if (linkedIds.length > 0) {
        const { data: inquiries, error: inqError } = await supabase
          .from('inquiries')
          .select('id, platform_type, platform_detail, status, brief_notes, site_notes')
          .in('id', linkedIds)
          .eq('platform_type', 'email_forward');

        if (!inqError && inquiries) {
          inquiriesMap = Object.fromEntries(inquiries.map((inq) => [inq.id, inq]));
        }
      }

      // Step 3: Map and filter — only include items whose linked inquiry
      // is confirmed as email_forward (or has no linked inquiry yet)
      const mapped = intakeData
        .filter((row) => {
          if (!row.linked_inquiry_id) return true; // new intake, not linked yet
          return !!inquiriesMap[row.linked_inquiry_id];
        })
        .map((row) => ({
          ...mapIntakeFromDb(row),
          linkedInquiry: row.linked_inquiry_id
            ? inquiriesMap[row.linked_inquiry_id] || null
            : null,
        }));

      setIntakeMessages(mapped);
      return { success: true, data: mapped };
    } catch (err) {
      console.error('[InquiryIntake] fetchNeedsReview error:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Count "needs review" intake messages ───────────────────────────
  // Lightweight head-count query (no rows returned) — powers the nav badge.
  const fetchNeedsReviewCount = useCallback(async () => {
    try {
      const { count, error: countErr } = await supabase
        .from('inquiry_intake_messages')
        .select('id', { count: 'exact', head: true })
        .eq('parse_status', 'needs_review');
      if (countErr) throw countErr;
      setNeedsReviewCount(count || 0);
    } catch (err) {
      console.error('[InquiryIntake] fetchNeedsReviewCount error:', err);
    }
  }, []);

  // ── Fetch all intake messages (for admin view, any status) ────────
  const fetchAll = useCallback(async (statusFilter = null) => {
    try {
      setLoading(true);
      let query = supabase
        .from('inquiry_intake_messages')
        .select('*')
        .order('received_at', { ascending: false });

      if (statusFilter) query = query.eq('parse_status', statusFilter);

      const { data, error: fetchError } = await query;
      if (fetchError) throw fetchError;

      const mapped = (data || []).map(mapIntakeFromDb);
      setIntakeMessages(mapped);
      return { success: true, data: mapped };
    } catch (err) {
      console.error('[InquiryIntake] fetchAll error:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Approve intake (admin only — RLS enforces this) ────────────────
  // Sets intake.parse_status='approved'
  // Sets inquiry.platform_detail='gmail_intake_approved' (keeps platform_type)
  // Does NOT change inquiry.status.
  const approveIntake = useCallback(
    async (intakeId) => {
      try {
        // 1. Update intake status
        const { error: intakeErr } = await supabase
          .from('inquiry_intake_messages')
          .update({ parse_status: 'approved' })
          .eq('id', intakeId);

        if (intakeErr) throw intakeErr;

        // 2. Update linked inquiry's platform_detail
        const { data: intake } = await supabase
          .from('inquiry_intake_messages')
          .select('linked_inquiry_id')
          .eq('id', intakeId)
          .single();

        if (intake?.linked_inquiry_id) {
          const { error: inqErr } = await supabase
            .from('inquiries')
            .update({ platform_detail: 'gmail_intake_approved' })
            .eq('id', intake.linked_inquiry_id);

          if (inqErr) console.warn('[InquiryIntake] inquiry update warning:', inqErr);
        }

        await fetchNeedsReview();
        return { success: true };
      } catch (err) {
        console.error('[InquiryIntake] approveIntake error:', err);
        return { success: false, error: err.message };
      }
    },
    [fetchNeedsReview]
  );

  // ── Reject intake (admin only — RLS enforces this) ─────────────────
  // Sets intake.parse_status='rejected'
  // Sets inquiry.status='archived', archived=true
  // Appends rejection note to site_notes.
  const rejectIntake = useCallback(
    async (intakeId, reason = '') => {
      try {
        // 1. Get linked inquiry ID
        const { data: intake } = await supabase
          .from('inquiry_intake_messages')
          .select('linked_inquiry_id')
          .eq('id', intakeId)
          .single();

        // 2. Update intake status
        const { error: intakeErr } = await supabase
          .from('inquiry_intake_messages')
          .update({ parse_status: 'rejected' })
          .eq('id', intakeId);

        if (intakeErr) throw intakeErr;

        // 3. Archive the linked inquiry + append rejection note to site_notes
        if (intake?.linked_inquiry_id) {
          const { data: inq } = await supabase
            .from('inquiries')
            .select('site_notes')
            .eq('id', intake.linked_inquiry_id)
            .single();

          const existingNotes = inq?.site_notes || '';
          const rejectionNote = `\n[Rejected from email intake${reason ? ': ' + reason : ''}]`;

          const { error: inqErr } = await supabase
            .from('inquiries')
            .update({
              status: 'archived',
              archived: true,
              site_notes: existingNotes + rejectionNote,
            })
            .eq('id', intake.linked_inquiry_id);

          if (inqErr) console.warn('[InquiryIntake] inquiry archive warning:', inqErr);
        }

        await fetchNeedsReview();
        return { success: true };
      } catch (err) {
        console.error('[InquiryIntake] rejectIntake error:', err);
        return { success: false, error: err.message };
      }
    },
    [fetchNeedsReview]
  );

  // ── Note: Ingestion is handled by Google Apps Script (runs on timer).
  // There is no triggerIngestion() call — the Edge Function is a webhook
  // that receives POSTs from Apps Script, not something invoked from UI.
  // The "Refresh" button simply re-fetches from the database.

  // ── Fetch all attachments linked to a specific inquiry ───────────
  const fetchAttachmentsForInquiry = useCallback(async (inquiryId) => {
    if (!inquiryId) return [];
    try {
      const { data, error: fetchErr } = await supabase
        .from('inquiry_intake_messages')
        .select('attachments')
        .eq('linked_inquiry_id', inquiryId);
      if (fetchErr) throw fetchErr;
      return (data || []).flatMap((row) => row.attachments || []);
    } catch (err) {
      console.error('[InquiryIntake] fetchAttachmentsForInquiry error:', err);
      return [];
    }
  }, []);

  // ── Get attachment signed URL (1-hour expiry) ──────────────────────
  // Storage bucket is private. We generate signed URLs on demand.
  const getAttachmentUrl = useCallback(async (storagePath) => {
    try {
      if (!storagePath || storagePath.startsWith('error:')) {
        return { success: false, error: 'Invalid storage path' };
      }

      const { data, error: urlErr } = await supabase.storage
        .from('inquiry-intake')
        .createSignedUrl(storagePath, 3600); // 1 hour

      if (urlErr) throw urlErr;
      return { success: true, url: data.signedUrl };
    } catch (err) {
      console.error('[InquiryIntake] getAttachmentUrl error:', err);
      return { success: false, error: err.message };
    }
  }, []);

  // ── Initial load ──────────────────────────────────────────────────
  useEffect(() => {
    if (!enabled) return;
    if (countOnly) fetchNeedsReviewCount();
    else fetchNeedsReview();
  }, [enabled, countOnly, fetchNeedsReview, fetchNeedsReviewCount]);

  return {
    intakeMessages,
    needsReviewCount,
    loading,
    error,
    fetchNeedsReview,
    fetchNeedsReviewCount,
    fetchAll,
    approveIntake,
    rejectIntake,
    getAttachmentUrl,
    fetchAttachmentsForInquiry,
    refetch: fetchNeedsReview,
  };
};

export default useSupabaseInquiryIntake;
